import { useRouter } from 'next/router';
import {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import type { ServiceLevel } from 'types/enums';
import type { User } from 'types/user';
import { i18n as I18n } from 'utils/i18n';
import { refreshSession } from 'utils/refreshSession';
import ClientFactory from '../../utils/apiClient';
import { isCustomer } from '../../utils/roles';
import reducer, { actions, initialState } from './reducer';

export interface AuthActions {
  signIn: (email: string, password: string) => Promise<void | null>;
  signOut: () => Promise<void>;
  resendEmailConfirmationLink: (email: string) => void;
  confirmEmail: (confirmEmailToken: string) => void;
  setReturnUrl: (url: string) => void;
  resetPassword: (email: string) => Promise<void>;
  changePassword: (
    password: string,
    confirmPassword: string,
    passwordResetToken: string
  ) => void;
  setToken: (token: string) => void;
  // Resolves to the new token, or undefined if the refresh failed.
  refreshAuthToken: () => Promise<string | undefined>;
}

// The `/v1/users/me` user shape held by the auth context. Note `role` is an
// object here, matching the canonical User shape. AuthUser is kept as an
// alias so existing references to the /users/me user keep reading.
export type AuthUser = User;

export interface AuthContextValue {
  loggedIn: boolean;
  loading?: boolean;
  accountApproved?: boolean;
  token?: string;
  user: AuthUser | null;
  // The user's primary client id, or null when signed out.
  currentClient: number | null;
  clientServiceLevel?: ServiceLevel | null;
  role: string;
  bookableServices: number[];
  // A translated, user-facing message.
  errorMessage?: string | null;
  acceptedLatestConditions?: boolean;
  refreshingToken?: boolean;
  actions: AuthActions;
}

export const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  loggedIn: false,
  accountApproved: false,
  token: undefined,
  user: null,
  currentClient: null,
  role: 'Booker',
  clientServiceLevel: null,
  bookableServices: [],
  actions: {
    signIn: async () => {},
    signOut: async () => {},
    resendEmailConfirmationLink: () => {},
    confirmEmail: () => {},
    setReturnUrl: () => {},
    resetPassword: async () => {},
    changePassword: () => {},
    setToken: () => {},
    refreshAuthToken: () => Promise.reject(new Error('Not logged in')),
  },
});

const emptyList: number[] = [];

const AuthProvider = ({
  token,
  children,
}: {
  token?: string;
  children?: ReactNode;
}) => {
  const [
    { returnUrl, errorMessage, user, token: authToken, refreshingToken },
    dispatch,
  ] = useReducer(reducer, initialState);
  const router = useRouter();

  const client = useMemo(() => ClientFactory(authToken), [authToken]);

  const signOut = useCallback(async () => {
    dispatch({ type: actions.SIGN_OUT });

    const { status } = await ClientFactory(authToken).get(
      `/v1/users/auth/logout`
    );

    if (status === 204) {
      dispatch({
        type: actions.SIGN_OUT_SUCCESS,
      });

      router.push('/');
    }
  }, [authToken, router]);

  useEffect(() => {
    if (user?.role && !isCustomer(user.role.displayName)) {
      signOut();
    }
  }, [signOut, user]);

  const setToken = useCallback(async (token: string) => {
    dispatch({ type: actions.SET_TOKEN, payload: token });

    const { data } = await ClientFactory(token).get(`/v1/users/me`);
    dispatch({
      type: actions.SET_USER,
      payload: data,
    });
  }, []);

  useEffect(() => {
    const refreshToken = async () => {
      if (token) {
        await setToken(token);
      }
    };

    refreshToken();
  }, [setToken, token]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      dispatch({ type: actions.SIGN_IN });

      // `returnUrl` is null until something sets it, which the guard below
      // already treats as "use the default path".
      const pathNameFor = (returnUrl: string | null) => {
        const pathPrefixes = [
          '/register',
          '/change-password',
          '/confirm-email',
        ];

        const isDefaultPath =
          !returnUrl ||
          pathPrefixes.some(prefix => returnUrl.startsWith(prefix));

        const url = isDefaultPath ? '/' : decodeURIComponent(returnUrl);

        const [pathname, hashQuery] = url.split('#');
        const [hash, query] = (hashQuery || '').split('?');

        return { pathname, hash: hash || '', query: query || '' };
      };

      try {
        const { data } = await client.post(`/v1/users/auth/login`, {
          email,
          password,
          scope: 'client',
        });

        if (data.token) {
          setToken(data.token);

          dispatch({
            type: actions.SIGN_IN_SUCCESS,
          });

          router.push(pathNameFor(returnUrl));
        }

        return null;
      } catch (_e) {
        return dispatch({
          type: actions.SIGN_IN_ERROR,
          payload: I18n.t('sign_in.validations.bad_credentials'),
        });
      }
    },
    [setToken, router, returnUrl, client]
  );

  const refreshAuthToken = useCallback(async () => {
    dispatch({ type: actions.REFRESH_TOKEN });

    try {
      // Shared with the refresh _app runs on page load. Both spend the same
      // rotating cookie, and on the pending-payment page both fire — see
      // utils/refreshSession.
      const token = await refreshSession();

      dispatch({ type: actions.SET_TOKEN, payload: token });

      client.interceptors.request.use(config => {
        config.headers.set('Authorization', `Bearer ${token}`);
        return config;
      });

      dispatch({ type: actions.REFRESH_TOKEN_SUCCESS });

      const { data: user } = await client.get(`/v1/users/me`);

      dispatch({
        type: actions.SET_USER,
        payload: user,
      });

      return token;
    } catch (_e) {
      dispatch({
        type: actions.SIGN_IN_ERROR,
        payload: I18n.t('sign_in.validations.bad_credentials'),
      });

      return undefined;
    }
  }, [client]);

  const resendEmailConfirmationLink = useCallback(
    (email: string) => {
      dispatch({ type: actions.RESEND_CONFIRMATION_EMAIL });

      client
        .get('/v1/users/registrations/resend_validation_email', {
          params: { email },
        })
        .catch(() => {})
        .finally(() => {
          dispatch({ type: actions.RESEND_CONFIRMATION_EMAIL_SUCCESS });

          window.location.href = '/resend-confirmation';
        });
    },
    [client]
  );

  const setReturnUrl = useCallback((url: string) => {
    dispatch({
      type: actions.SET_RETURN_URL,
      payload: url,
    });
  }, []);

  const resetPassword = useCallback(
    async (email: string) => {
      dispatch({ type: actions.RESET_PASSWORD });

      client
        .post('/v1/users/auth/reset_password_token', {
          email,
        })
        .then(() => {
          dispatch({ type: actions.RESET_PASSWORD_SUCCESS });

          window.location.href = '/reset-password-confirmation';
        })
        .catch(
          ({
            response: {
              data: { message },
            },
          }) => {
            return dispatch({
              type: actions.RESET_PASSWORD_ERROR,
              payload: message,
            });
          }
        );
    },
    [client]
  );

  const changePassword = useCallback(
    (password: string, confirmPassword: string, passwordResetToken: string) => {
      dispatch({ type: actions.CHANGE_PASSWORD });

      client
        .post('/v1/users/auth/reset_password', {
          password,
          passwordConfirmation: confirmPassword,
          resetPasswordToken: passwordResetToken,
        })
        .then(() => {
          dispatch({ type: actions.CHANGE_PASSWORD_SUCCESS });

          window.location.href = '/change-password-confirmation';
        })
        .catch(
          ({
            response: {
              data: { message },
            },
          }) => {
            return dispatch({
              type: actions.CHANGE_PASSWORD_ERROR,
              payload: message,
            });
          }
        );
    },
    [client]
  );

  const confirmEmail = useCallback(
    (confirmEmailToken: string) => {
      dispatch({ type: actions.CONFIRM_EMAIL });

      client
        .post('/v1/users/registrations/verify_account', {
          token: confirmEmailToken,
        })
        .then(() => {
          dispatch({ type: actions.CONFIRM_EMAIL_SUCCESS });
        })
        .catch(
          ({
            response: {
              data: { message },
            },
          }) => {
            return dispatch({
              type: actions.CONFIRM_EMAIL_ERROR,
              payload: message,
            });
          }
        );
    },
    [client]
  );
  const actionMethods = {
    signIn,
    signOut,
    resendEmailConfirmationLink,
    confirmEmail,
    setReturnUrl,
    resetPassword,
    changePassword,
    refreshAuthToken,
    setToken,
  };

  return (
    <AuthContext.Provider
      value={{
        loggedIn: !!user,
        errorMessage,
        accountApproved: user?.approved,
        token: authToken,
        user,
        currentClient: user?.primaryClientId ?? null,
        role: user ? user.role.displayName : 'Booker',
        clientServiceLevel: user?.clientServiceLevel ?? null,
        bookableServices: user
          ? user.clientServices.map((s: { id: number }) => s.id)
          : emptyList,
        acceptedLatestConditions: user?.acceptedLatestConditions,
        refreshingToken,
        actions: actionMethods,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
